import { useState, useEffect } from 'react';

// ============================================
// Slot catalog loader.
//
// The catalog is ~5,700 slots harvested from Shuffle's own games API. It used to
// be a bundled JS module, but at that size it was pure weight on every page load
// for a feature most visitors never open — so it now ships as a cached JSON
// asset that's fetched once, on demand, and shared across every consumer.
//
// Card art is served straight from Shuffle's CDN rather than self-hosted, so the
// repo carries no image payload and the art never goes stale.
//
// To refresh: re-run the harvest script against shuffle.com and regenerate
// public/slots-catalog.json.
// ============================================

const CATALOG_URL = '/slots-catalog.json';

// Module-level cache: the fetch happens once per page load no matter how many
// components ask for it.
let cache = null;
let inflight = null;
const listeners = new Set();

function expand(payload) {
  const providers = payload.providers || [];
  const base = payload.imageBase || 'https://shuffle-com.imgix.net/';
  return (payload.slots || []).map(r => ({
    name: r.n,
    provider: providers[r.p] || 'Unknown',
    slug: r.s,
    // `img` is a legacy self-hosted path kept for slots Shuffle has delisted;
    // everything current resolves against the CDN.
    image: r.img || (r.i ? `${base}${r.i}?auto=format&w=320` : null),
    rtp: typeof r.r === 'number' ? r.r : null,
    // true when Shuffle tags it, otherwise null for "unknown" — never false.
    // Their BONUS_BUY category is trustworthy when present but very incomplete
    // when absent, so we show a badge or nothing at all, never "no bonus buy".
    bonusBuy: r.b ? true : null,
    accentColor: r.c || null,
    legacy: !!r.legacy
  }));
}

export function loadSlotCatalog() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetch(CATALOG_URL)
    .then(res => (res.ok ? res.json() : Promise.reject(new Error(`catalog ${res.status}`))))
    .then(payload => {
      cache = expand(payload);
      listeners.forEach(fn => fn(cache));
      return cache;
    })
    .catch(err => {
      console.error('Slot catalog failed to load:', err.message);
      cache = [];            // fail soft — pickers show empty rather than crashing
      listeners.forEach(fn => fn(cache));
      return cache;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function useSlotCatalog() {
  const [slots, setSlots] = useState(cache || []);

  useEffect(() => {
    if (cache) { setSlots(cache); return; }
    let alive = true;
    const onLoad = list => { if (alive) setSlots(list); };
    listeners.add(onLoad);
    loadSlotCatalog();
    return () => { alive = false; listeners.delete(onLoad); };
  }, []);

  return slots;
}

export default useSlotCatalog;
